/**
 * Inquiry form field behaviour: US address autocomplete, and a date picker
 * styled to the page instead of the browser's own calendar.
 *
 * Both elements only decorate the input they wrap. If this script never loads,
 * the location field is a plain text box and the timeline field is a native
 * date input — the form still works and still submits the same values.
 */

/* Address autocomplete ----------------------------------------------------- */

const MIN_QUERY = 3;
const DEBOUNCE = 280;
const MAX_RESULTS = 6;

/* Results are restricted to the United States: the bbox biases the provider
   and the country check drops anything that still comes back from across a
   border inside that box. */
const US_BBOX = '-179.15,18.91,-66.93,71.39';

/* Results are ranked nearest this point first. Biasing, not restricting — a
   Los Angeles office still resolves, it just sorts below local matches.
   Change these two numbers to move the centre of gravity. */
const BIAS_LAT = 40.7128;
const BIAS_LON = -74.006;
const BIAS_RADIUS_M = 50000;

/* US street types, abbreviation to canonical form. Used twice: to expand what
   the visitor typed before it is sent, and to reject results of the wrong
   type — "46 howard st" must not offer Howard Avenue or Howard Parkway. */
const STREET_TYPES = {
  st: 'street', street: 'street',
  ave: 'avenue', av: 'avenue', avenue: 'avenue',
  rd: 'road', road: 'road',
  blvd: 'boulevard', boulevard: 'boulevard',
  dr: 'drive', drive: 'drive',
  ln: 'lane', lane: 'lane',
  ct: 'court', court: 'court',
  pl: 'place', place: 'place',
  pkwy: 'parkway', pky: 'parkway', parkway: 'parkway',
  hwy: 'highway', highway: 'highway',
  sq: 'square', square: 'square',
  ter: 'terrace', terrace: 'terrace',
  cir: 'circle', circle: 'circle',
  trl: 'trail', trail: 'trail',
  way: 'way',
};

const words = (s) =>
  String(s || '').toLowerCase().replace(/[.,]/g, ' ').split(/\s+/).filter(Boolean);

/** "46 howard st" -> "46 howard street", so the provider matches the full word. */
const expandQuery = (q) => words(q).map((w) => STREET_TYPES[w] || w).join(' ');

const queryStreetType = (q) => {
  for (const w of words(q)) if (STREET_TYPES[w]) return STREET_TYPES[w];
  return null;
};

/** Read the type off the end of a street name: "Harry Howard Avenue" -> avenue. */
const entryStreetType = (street) => {
  const w = words(street);
  for (let i = w.length - 1; i >= 0; i -= 1) if (STREET_TYPES[w[i]]) return STREET_TYPES[w[i]];
  return null;
};

const queryNumber = (q) => {
  const m = /(^|\s)(\d+[a-z]?)(\s|$)/i.exec(String(q));
  return m ? m[2].toLowerCase() : null;
};

/* Rough great-circle-ish distance in miles. Only ever used to sort and to show
   an approximate figure, so the cosine correction is taken at the bias
   latitude rather than per point. */
const LAT_SCALE = Math.cos((40.7128 * Math.PI) / 180);
const milesFrom = (lat, lon) =>
  Math.hypot(lat - BIAS_LAT, (lon - BIAS_LON) * LAT_SCALE) * 69.0;

/* Photon gives the state as a full name on some records and a code on others
   ("New York" vs "NY", "Massachusetts" vs "MA"). Everything is reduced to the
   code, which also stops a city collapsing into its own state: "New York" the
   city was rendering with an empty second line because the state string was
   identical to the name. */
const STATE_ABBR = {
  alabama: 'AL', alaska: 'AK', arizona: 'AZ', arkansas: 'AR', california: 'CA',
  colorado: 'CO', connecticut: 'CT', delaware: 'DE', florida: 'FL', georgia: 'GA',
  hawaii: 'HI', idaho: 'ID', illinois: 'IL', indiana: 'IN', iowa: 'IA',
  kansas: 'KS', kentucky: 'KY', louisiana: 'LA', maine: 'ME', maryland: 'MD',
  massachusetts: 'MA', michigan: 'MI', minnesota: 'MN', mississippi: 'MS', missouri: 'MO',
  montana: 'MT', nebraska: 'NE', nevada: 'NV', 'new hampshire': 'NH', 'new jersey': 'NJ',
  'new mexico': 'NM', 'new york': 'NY', 'north carolina': 'NC', 'north dakota': 'ND', ohio: 'OH',
  oklahoma: 'OK', oregon: 'OR', pennsylvania: 'PA', 'rhode island': 'RI', 'south carolina': 'SC',
  'south dakota': 'SD', tennessee: 'TN', texas: 'TX', utah: 'UT', vermont: 'VT',
  virginia: 'VA', washington: 'WA', 'west virginia': 'WV', wisconsin: 'WI', wyoming: 'WY',
  'district of columbia': 'DC', 'puerto rico': 'PR', guam: 'GU', 'virgin islands': 'VI',
};

function stateCode(value) {
  const s = String(value || '').trim();
  if (!s) return '';
  if (s.length === 2) return s.toUpperCase();
  return STATE_ABBR[s.toLowerCase()] || s;
}

/**
 * Photon returns GeoJSON. Split it the way an address list reads: the street
 * or place name on top, the city and state under it.
 */
function photonEntry(feature) {
  const p = feature.properties || {};
  const isPlace = p.osm_key === 'place';
  const state = stateCode(p.state);
  const street = p.street || '';

  // A city, borough or neighbourhood is named by `name` and has no street of
  // its own; an address is the house number and street.
  const main = isPlace
    ? p.name || ''
    : [p.housenumber, street].filter(Boolean).join(' ') || p.name || '';
  if (!main) return null;

  const locality = isPlace
    ? p.county || p.city || ''
    : p.city || p.town || p.village || p.county || '';

  const secondary = [...new Set([locality, state].filter(Boolean))]
    .filter((part) => part !== main)
    .join(', ');

  const [lon, lat] = feature.geometry?.coordinates || [];
  return {
    main,
    secondary,
    value: [main, secondary].filter(Boolean).join(', '),
    type: entryStreetType(street || (isPlace ? '' : p.name)),
    number: String(p.housenumber || '').toLowerCase(),
    isPlace,
    isStreet: p.osm_key === 'highway',
    hasNumber: !!p.housenumber,
    miles: lat == null ? null : milesFrom(lat, lon),
  };
}

/**
 * Sorting bucket. With a house number typed, addresses come first. Without one
 * the visitor is naming a place, so cities and neighbourhoods beat the
 * footpaths and bridges that otherwise crowd the top for "brooklyn".
 */
function kindRank(entry, hasNumber) {
  if (hasNumber) {
    if (entry.hasNumber) return 0;
    if (entry.isStreet) return 1;
    return entry.isPlace ? 2 : 3;
  }
  if (entry.isPlace) return 0;
  return entry.isStreet ? 2 : 1;
}

class CoAddressAutocomplete extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('input');
    if (!this.input || this.dataset.ready) return;
    this.dataset.ready = 'true';

    this.provider = this.dataset.provider || 'photon';
    // Switched off in the editor: leave the input alone, so nothing the visitor
    // types leaves the page.
    if (this.provider === 'off') return;

    this.apiKey = this.dataset.apiKey || '';
    // Google Places needs a key; without one, fall back rather than 400 on every keystroke.
    if (this.provider === 'google' && !this.apiKey) this.provider = 'photon';

    this.index = -1;
    this.items = [];
    this.controller = null;
    this.sessionToken = crypto.randomUUID?.();

    this.buildList();

    this.onInput = this.onInput.bind(this);
    this.onKeydown = this.onKeydown.bind(this);
    this.onBlur = this.onBlur.bind(this);

    this.input.setAttribute('role', 'combobox');
    this.input.setAttribute('aria-autocomplete', 'list');
    this.input.setAttribute('aria-expanded', 'false');
    this.input.setAttribute('aria-controls', this.list.id);
    this.input.setAttribute('autocomplete', 'off');

    this.input.addEventListener('input', this.onInput);
    this.input.addEventListener('keydown', this.onKeydown);
    this.input.addEventListener('blur', this.onBlur);
  }

  disconnectedCallback() {
    this.controller?.abort();
  }

  buildList() {
    this.list = document.createElement('ul');
    this.list.className = 'co__suggestions';
    this.list.id = `${this.input.id || 'co-address'}-suggestions`;
    this.list.setAttribute('role', 'listbox');
    this.list.hidden = true;
    // mousedown, not click: blur fires first on click and would close the list.
    this.list.addEventListener('mousedown', (event) => {
      const li = event.target.closest('li');
      if (!li) return;
      event.preventDefault();
      this.choose(Number(li.dataset.index));
    });
    this.appendChild(this.list);
  }

  onInput() {
    clearTimeout(this.timer);
    const query = this.input.value.trim();
    if (query.length < MIN_QUERY) return this.close();
    this.timer = setTimeout(() => this.search(query), DEBOUNCE);
  }

  async search(query) {
    // One request in flight at a time; a stale response must not overwrite a newer one.
    this.controller?.abort();
    this.controller = new AbortController();
    try {
      const results = await this.fetchResults(query, this.controller.signal);
      if (this.input.value.trim() !== query) return;
      this.render(results);
    } catch (error) {
      if (error.name !== 'AbortError') this.close();
    }
  }

  async fetchResults(query, signal) {
    if (this.provider === 'google') return this.fetchGoogle(query, signal);

    // Over-fetch: the type filter and de-duplication below discard a lot, and
    // the good match is often well down Photon's own ordering.
    const url =
      `https://photon.komoot.io/api/?q=${encodeURIComponent(expandQuery(query))}` +
      `&limit=40&bbox=${US_BBOX}&lat=${BIAS_LAT}&lon=${BIAS_LON}`;
    const data = await fetch(url, { signal }).then((r) => r.json());

    const entries = (data.features || [])
      .filter((f) => f.properties?.countrycode === 'US')
      .map(photonEntry)
      .filter(Boolean);

    // If the visitor named a street type, anything of another type is simply
    // wrong. Fall back to the unfiltered set rather than showing nothing.
    const wantType = queryStreetType(query);
    const typed = wantType ? entries.filter((e) => e.type === wantType) : entries;
    const pool = typed.length ? typed : entries;

    const wantNumber = queryNumber(query);
    pool.sort((a, b) => {
      const ak = kindRank(a, !!wantNumber);
      const bk = kindRank(b, !!wantNumber);
      if (ak !== bk) return ak - bk;
      if (wantNumber) {
        const am = a.number === wantNumber ? 0 : 1;
        const bm = b.number === wantNumber ? 0 : 1;
        if (am !== bm) return am - bm;
      }
      return (a.miles ?? 1e9) - (b.miles ?? 1e9);
    });

    const seen = new Set();
    return pool.filter((e) => !seen.has(e.value) && seen.add(e.value)).slice(0, MAX_RESULTS);
  }

  /**
   * Places API (New). Note the host: the older
   * maps.googleapis.com/maps/api/place/autocomplete/json is a server-side
   * endpoint and browsers are blocked from it by CORS. This one is built for
   * browser calls and takes the key in a header rather than the query string.
   */
  async fetchGoogle(query, signal) {
    const body = {
      input: query,
      includedRegionCodes: ['us'],
      locationBias: {
        circle: {
          center: { latitude: BIAS_LAT, longitude: BIAS_LON },
          radius: BIAS_RADIUS_M,
        },
      },
    };
    // Groups the keystrokes of one lookup into a single billable session.
    if (this.sessionToken) body.sessionToken = this.sessionToken;

    const res = await fetch('https://places.googleapis.com/v1/places:autocomplete', {
      method: 'POST',
      signal,
      headers: {
        'Content-Type': 'application/json',
        'X-Goog-Api-Key': this.apiKey,
      },
      body: JSON.stringify(body),
    });
    if (!res.ok) throw new Error(`places ${res.status}`);
    const data = await res.json();

    return (data.suggestions || [])
      .map((s) => s.placePrediction)
      .filter(Boolean)
      .slice(0, MAX_RESULTS)
      .map((p) => ({
        main: p.structuredFormat?.mainText?.text || p.text?.text || '',
        secondary: p.structuredFormat?.secondaryText?.text || '',
        value: p.text?.text || p.structuredFormat?.mainText?.text || '',
      }))
      .filter((e) => e.value);
  }

  render(results) {
    this.items = results;
    this.index = -1;
    if (!results.length) return this.close();
    const esc = (s) => String(s || '').replace(/&/g, '&amp;').replace(/</g, '&lt;');
    this.list.innerHTML = results
      .map(
        (entry, i) =>
          `<li role="option" id="${this.list.id}-${i}" data-index="${i}" aria-selected="false">` +
          `<svg class="co__suggestion-pin" viewBox="0 0 16 16" aria-hidden="true" focusable="false">` +
          `<path d="M8 1.6a4.4 4.4 0 0 0-4.4 4.4c0 3.3 4.4 8.4 4.4 8.4s4.4-5.1 4.4-8.4A4.4 4.4 0 0 0 8 1.6Z" ` +
          `fill="none" stroke="currentColor" stroke-width="1.2"/>` +
          `<circle cx="8" cy="6" r="1.5" fill="currentColor"/></svg>` +
          `<span class="co__suggestion-text"><span class="co__suggestion-main">${esc(entry.main)}</span>` +
          (entry.secondary
            ? `<span class="co__suggestion-secondary">${esc(entry.secondary)}</span>`
            : '') +
          `</span>` +
          (entry.miles != null
            ? `<span class="co__suggestion-distance">${
                entry.miles < 10 ? entry.miles.toFixed(1) : Math.round(entry.miles)
              } mi</span>`
            : '') +
          `</li>`,
      )
      .join('');
    this.list.hidden = false;
    this.input.setAttribute('aria-expanded', 'true');
  }

  close() {
    this.list.hidden = true;
    this.list.innerHTML = '';
    this.items = [];
    this.index = -1;
    this.input.setAttribute('aria-expanded', 'false');
    this.input.removeAttribute('aria-activedescendant');
  }

  choose(i) {
    if (this.items[i] == null) return;
    this.input.value = this.items[i].value;
    this.input.dispatchEvent(new Event('change', { bubbles: true }));
    // A Places session ends at selection; the next lookup starts a new one.
    this.sessionToken = crypto.randomUUID?.();
    this.close();
    this.input.focus();
  }

  highlight(next) {
    if (!this.items.length) return;
    this.index = (next + this.items.length) % this.items.length;
    [...this.list.children].forEach((li, i) => {
      const on = i === this.index;
      li.classList.toggle('is-active', on);
      li.setAttribute('aria-selected', String(on));
    });
    this.input.setAttribute('aria-activedescendant', `${this.list.id}-${this.index}`);
  }

  onKeydown(event) {
    if (this.list.hidden) return;
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      this.highlight(this.index + 1);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      this.highlight(this.index - 1);
    } else if (event.key === 'Enter' && this.index > -1) {
      // Only swallow Enter when a suggestion is highlighted, so the form still
      // submits normally the rest of the time.
      event.preventDefault();
      this.choose(this.index);
    } else if (event.key === 'Escape') {
      this.close();
    }
  }

  onBlur() {
    setTimeout(() => this.close(), 120);
  }
}

/* Date picker -------------------------------------------------------------- */

const DAY_NAMES = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'];
const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const iso = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

const parseIso = (value) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value || '');
  if (!m) return null;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return Number.isNaN(d.getTime()) ? null : d;
};

const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());

/**
 * A calendar panel drawn from the section's own custom properties, replacing
 * the browser's calendar — which is chrome and cannot be styled.
 *
 * The real <input type="date"> stays the form field, so the submitted value is
 * still an ISO date. The input is made readonly only once this runs, which is
 * what suppresses the native picker on both desktop and mobile; without the
 * script it stays a working native date field.
 */
class CoDateField extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('input');
    if (!this.input || this.dataset.ready) return;
    this.dataset.ready = 'true';

    // Suppress the native picker. Readonly still submits and still shows the
    // value; it only stops the browser opening its own calendar.
    this.input.readOnly = true;
    this.input.setAttribute('aria-haspopup', 'dialog');
    this.input.setAttribute('aria-expanded', 'false');

    this.today = startOfDay(new Date());
    this.min = parseIso(this.dataset.min) || (this.dataset.past === 'true' ? null : this.today);
    this.view = parseIso(this.input.value) || this.today;
    this.open = false;

    this.buildPanel();

    this.onDocDown = this.onDocDown.bind(this);
    this.input.addEventListener('click', () => this.toggle());
    this.input.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') {
        e.preventDefault();
        this.toggle(true);
      }
    });
  }

  disconnectedCallback() {
    document.removeEventListener('mousedown', this.onDocDown);
  }

  buildPanel() {
    this.panel = document.createElement('div');
    this.panel.className = 'co__calendar';
    this.panel.setAttribute('role', 'dialog');
    this.panel.setAttribute('aria-label', 'Choose a date');
    this.panel.hidden = true;
    this.panel.innerHTML = `
      <div class="co__calendar-head">
        <button type="button" class="co__calendar-nav" data-step="-1" aria-label="Previous month">&#8249;</button>
        <div class="co__calendar-title">
          <select class="co__calendar-month" aria-label="Month"></select>
          <select class="co__calendar-year" aria-label="Year"></select>
        </div>
        <button type="button" class="co__calendar-nav" data-step="1" aria-label="Next month">&#8250;</button>
      </div>
      <div class="co__calendar-days">${DAY_NAMES.map((d) => `<span>${d}</span>`).join('')}</div>
      <div class="co__calendar-grid" role="grid"></div>
      <div class="co__calendar-foot">
        <button type="button" class="co__calendar-clear">Clear</button>
      </div>`;
    this.appendChild(this.panel);

    this.grid = this.panel.querySelector('.co__calendar-grid');
    this.monthSelect = this.panel.querySelector('.co__calendar-month');
    this.yearSelect = this.panel.querySelector('.co__calendar-year');

    this.monthSelect.innerHTML = MONTH_NAMES.map((m, i) => `<option value="${i}">${m}</option>`).join('');
    const first = (this.min || this.today).getFullYear();
    this.yearSelect.innerHTML = Array.from({ length: 8 }, (_, i) => first + i)
      .map((y) => `<option value="${y}">${y}</option>`)
      .join('');

    // No mousedown preventDefault here: it would stop the month and year
    // selects opening their native dropdown. The panel stays open because
    // onDocDown ignores clicks inside this element.
    this.panel.querySelectorAll('.co__calendar-nav').forEach((b) =>
      b.addEventListener('click', () => this.shift(Number(b.dataset.step))),
    );
    this.monthSelect.addEventListener('change', () => {
      this.view = new Date(this.view.getFullYear(), Number(this.monthSelect.value), 1);
      this.renderGrid();
    });
    this.yearSelect.addEventListener('change', () => {
      this.view = new Date(Number(this.yearSelect.value), this.view.getMonth(), 1);
      this.renderGrid();
    });
    this.panel.querySelector('.co__calendar-clear').addEventListener('click', () => {
      this.input.value = '';
      this.input.dispatchEvent(new Event('change', { bubbles: true }));
      this.close();
    });
    this.grid.addEventListener('click', (e) => {
      const b = e.target.closest('button[data-date]');
      if (b) this.select(b.dataset.date);
    });
  }

  shift(step) {
    this.view = new Date(this.view.getFullYear(), this.view.getMonth() + step, 1);
    this.renderGrid();
  }

  renderGrid() {
    const year = this.view.getFullYear();
    const month = this.view.getMonth();
    this.monthSelect.value = String(month);
    // A year outside the range the select offers would otherwise blank it.
    if (![...this.yearSelect.options].some((o) => o.value === String(year))) {
      this.yearSelect.insertAdjacentHTML('afterbegin', `<option value="${year}">${year}</option>`);
    }
    this.yearSelect.value = String(year);

    const firstOfMonth = new Date(year, month, 1);
    // Monday-first: getDay() is 0 for Sunday, which belongs at the end.
    const lead = (firstOfMonth.getDay() + 6) % 7;
    const days = new Date(year, month + 1, 0).getDate();
    const selected = parseIso(this.input.value);

    let html = '';
    for (let i = 0; i < lead; i += 1) html += '<span class="co__calendar-pad"></span>';
    for (let day = 1; day <= days; day += 1) {
      const date = new Date(year, month, day);
      const value = iso(date);
      const disabled = this.min && date < this.min;
      const classes = [
        'co__calendar-day',
        selected && value === iso(selected) ? 'is-selected' : '',
        value === iso(this.today) ? 'is-today' : '',
      ]
        .filter(Boolean)
        .join(' ');
      html +=
        `<button type="button" class="${classes}" data-date="${value}"` +
        `${disabled ? ' disabled' : ''} aria-label="${MONTH_NAMES[month]} ${day}, ${year}"` +
        `${selected && value === iso(selected) ? ' aria-current="date"' : ''}>${day}</button>`;
    }
    this.grid.innerHTML = html;
  }

  select(value) {
    this.input.value = value;
    this.input.dispatchEvent(new Event('change', { bubbles: true }));
    this.close();
  }

  toggle(force) {
    if (this.open) {
      // Re-opening an open panel would stack a second set of dismiss listeners
      // and orphan the first, so opening again is a no-op.
      if (force === true) return;
      return this.close();
    }
    this.view = parseIso(this.input.value) || this.view;
    this.renderGrid();
    this.panel.hidden = false;
    this.open = true;
    this.input.setAttribute('aria-expanded', 'true');
    this.onEsc = (e) => {
      if (e.key === 'Escape') this.close();
    };
    document.addEventListener('mousedown', this.onDocDown);
    document.addEventListener('keydown', this.onEsc);
  }

  close() {
    this.panel.hidden = true;
    this.open = false;
    this.input.setAttribute('aria-expanded', 'false');
    document.removeEventListener('mousedown', this.onDocDown);
    if (this.onEsc) {
      document.removeEventListener('keydown', this.onEsc);
      this.onEsc = null;
    }
  }

  onDocDown(event) {
    if (!this.contains(event.target)) this.close();
  }
}

/* Shared with co-map-picker.js so the dialog's search and the reverse geocode
   label addresses the same way. Both scripts are deferred and this one is
   emitted first, but the picker still falls back if it is missing. */
window.CoGeo = {
  stateCode,
  entry: photonEntry,
  label: (feature) => photonEntry(feature)?.value || '',
};

if (!customElements.get('co-address-autocomplete')) {
  customElements.define('co-address-autocomplete', CoAddressAutocomplete);
}

if (!customElements.get('co-date-field')) {
  customElements.define('co-date-field', CoDateField);
}
